// Molecular: Data loading and processing services for energy data

import { EnergyType, EnergyPlant, GemeindeData, GemeindeOnlineData, GemeindenOnlineFile, RawEnergyPlant } from '../types';
import { loadJsonData } from '../atomic/loaders';
import { transformPlantData } from '../atomic/transform';
import { getFromKV, isKVAvailable } from '../../kv/client';

// Cache for storing loaded data
import { dataCache } from './cache';

// Cache TTL in milliseconds
const GEMEINDE_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours for gemeinde data (updated more frequently)
const CACHE_KEY_PREFIX = 'econtrol_';

/**
 * Get localStorage usage statistics
 */
function getLocalStorageStats(): { used: number; total: number; percentage: number } {
  if (typeof window === 'undefined') return { used: 0, total: 0, percentage: 0 };
  
  try {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    
    // Most browsers limit localStorage to ~5-10MB
    const estimatedLimit = 5 * 1024 * 1024; // 5MB
    const percentage = Math.round((totalSize / estimatedLimit) * 100);
    
    return {
      used: totalSize,
      total: estimatedLimit,
      percentage: Math.min(percentage, 100)
    };
  } catch (error) {
    return { used: 0, total: 0, percentage: 0 };
  }
}

/**
 * Calculate when energy data expires (Ende Januar des nächsten Jahres)
 * Energy data is updated at the end of January each year with previous year's data
 */
function getEnergyDataExpirationTime(): number {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0 = January
  
  // If we're before February, data expires end of January this year
  // If we're in February or later, data expires end of January next year
  const expirationYear = currentMonth < 1 ? currentYear : currentYear + 1;
  
  // Set expiration to February 1st of expiration year (end of January)
  const expirationDate = new Date(expirationYear, 1, 1); // Month 1 = February
  
  return expirationDate.getTime();
}

// Feature flag for migrating to new data structure
const USE_ONLINE_DATA = process.env.NEXT_PUBLIC_USE_ONLINE_DATA === 'true' || false;

// JSON data paths
export const JSON_DATA_PATHS = {
  wasser: '/data/wasser.json',
  sonne: '/data/sonne.json',
  biogas: '/data/biogas.json',
  biomasse: '/data/biomasse.json',
  gemeindeData: '/data/gemeinde-data.json',
  gemeindenOnline: '/data/gemeinden_online.json'
};

// Type for cached energy data
interface CachedEnergyData {
  timestamp: number;
  data: Record<string, EnergyPlant[]>;
}

// Type for gemeinde data structure
interface GemeindeDataFile {
  gemeinden: GemeindeData[];
  plzToGemeinde?: Record<string, { gemeindeNummer: string; name: string; einwohner: number; haushalte: number }>; // Optional for backward compatibility
  gemeindeByNummer?: Record<string, GemeindeData>; // Optional - we're removing this
  statistics: {
    totalGemeinden: number;
    totalEinwohner: number;
    totalHaushalte: number;
    totalPlz: number;
    generatedAt: string;
  };
}

// Type for cached gemeinde data (legacy)
interface CachedGemeindeData extends GemeindeDataFile {
  timestamp: number;
}

// Type for cached gemeinde online data (new)
interface CachedGemeindeOnlineData extends GemeindenOnlineFile {
  timestamp: number;
  dataSource: 'kv' | 'local';
}

/**
 * Load energy data for multiple PLZ codes in a single batch request
 * This prevents hundreds of individual API calls and dramatically improves performance
 */
export async function loadEnergyDataBatch(plzList: string[], types?: EnergyType[]): Promise<Record<string, Record<string, EnergyPlant[]>>> {
  if (plzList.length === 0) return {};
  
  // Create a unique cache key for this batch
  const sortedPlzList = [...plzList].sort();
  const typesKey = types ? types.sort().join(',') : 'all';
  const batchCacheKey = `energy_batch_${sortedPlzList.join(',')}_${typesKey}`;
  
  // Check in-memory cache first
  const cachedData = dataCache[batchCacheKey] as CachedEnergyData | undefined;
  const energyExpirationTime = getEnergyDataExpirationTime();
  if (cachedData && cachedData.timestamp < energyExpirationTime) {
    return cachedData.data;
  }

  // Check for individual PLZ caches to avoid redundant requests
  const result: Record<string, Record<string, EnergyPlant[]>> = {};
  const uncachedPlzList: string[] = [];
  
  for (const plz of plzList) {
    const individualCacheKey = `energy_plz_${plz}_${typesKey}`;
    const individualCached = dataCache[individualCacheKey] as CachedEnergyData | undefined;
    
    if (individualCached && individualCached.timestamp < energyExpirationTime) {
      result[plz] = individualCached.data;
    } else {
      uncachedPlzList.push(plz);
    }
  }
  
  // If all PLZ are cached, return immediately
  if (uncachedPlzList.length === 0) {
    return result;
  }

  try {
    console.log(`[DataService] Fetching energy data for ${uncachedPlzList.length} PLZ codes in batch: ${uncachedPlzList.join(', ')}`);
    
    // Use batch API to get data for multiple PLZ
    const response = await fetch('/api/energy/batch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        plzList: uncachedPlzList,
        types: types || ['wasser', 'sonne', 'biogas', 'biomasse']
      })
    });
    
    if (!response.ok) {
      throw new Error(`Batch API Error: ${response.status} ${response.statusText}`);
    }
    
    const batchData = await response.json();
    
    // Transform and cache the batch data
    for (const [plz, energyData] of Object.entries(batchData)) {
      const transformedData: Record<string, EnergyPlant[]> = {};
      
      for (const [energyType, plants] of Object.entries(energyData as Record<string, any>)) {
        if (Array.isArray(plants)) {
          transformedData[energyType] = plants.map(plant => transformPlantData(plant as RawEnergyPlant, energyType as EnergyType));
        }
      }
      
      result[plz] = transformedData;
      
      // Cache individual PLZ data for future single requests
      const individualCacheKey = `energy_plz_${plz}_${typesKey}`;
      const cachedEnergyData: CachedEnergyData = {
        timestamp: Date.now(),
        data: transformedData
      };
      dataCache[individualCacheKey] = cachedEnergyData;
    }
    
    // Cache the entire batch result
    const batchCachedData: CachedEnergyData = {
      timestamp: Date.now(),
      data: result
    };
    dataCache[batchCacheKey] = batchCachedData;
    
    return result;
  } catch (error) {
    console.error(`Error loading batch energy data for PLZ: ${uncachedPlzList.join(', ')}:`, error);
    return result; // Return partial results from cache
  }
}

/**
 * Load energy data for a specific PLZ and type using server-side API
 * This prevents loading large JSON files (like 16MB sonne.json) in the browser
 */
export async function loadEnergyDataForPLZ(plz: string, type?: EnergyType): Promise<Record<string, EnergyPlant[]>> {
  // Use batch function for single PLZ to leverage shared caching
  const batchResult = await loadEnergyDataBatch([plz], type ? [type] : undefined);
  return batchResult[plz] || {};
}

/**
 * @deprecated Use loadEnergyDataForPLZ instead to prevent browser crashes from large files
 * Load energy data for a specific type with LocalStorage caching
 */
export async function loadEnergyData(type: EnergyType): Promise<Record<string, EnergyPlant[]>> {
  console.warn(`[DataService] loadEnergyData(${type}) is deprecated. Use loadEnergyDataForPLZ instead to prevent browser crashes.`);
  
  // For backward compatibility, return empty data to prevent loading large files
  return {};
}

/**
 * Check localStorage for cached data (simple, no chunking to prevent browser crashes)
 */
function getFromLocalStorage<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const fullKey = CACHE_KEY_PREFIX + key;
    const storedData = localStorage.getItem(fullKey);
    
    if (!storedData) return null;
    
    const data = JSON.parse(storedData);
    
    // Use appropriate expiration logic based on cache key
    if (key.startsWith('energy_')) {
      // Energy data expires at end of January each year
      const energyExpirationTime = getEnergyDataExpirationTime();
      if ((data as any).timestamp && (data as any).timestamp < energyExpirationTime) {
        return data;
      }
    } else {
      // Other data uses standard TTL
      if ((data as any).timestamp && (data as any).timestamp > Date.now() - GEMEINDE_CACHE_TTL) {
        return data;
      }
    }
    
    // Remove expired cache
    localStorage.removeItem(fullKey);
    return null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error reading from localStorage:', error);
    }
    return null;
  }
}

/**
 * Compress data using simple JSON compression (remove whitespace and repetitive keys)
 */
function compressData(data: any): string {
  try {
    // Use JSON.stringify with no spaces
    return JSON.stringify(data);
  } catch (error) {
    console.warn('[DataService] Error compressing data:', error);
    return JSON.stringify(data);
  }
}

// Chunking functions removed to prevent browser crashes from large files like sonne.json

// Chunking functions removed to prevent browser crashes from large files like sonne.json

// Chunking functions removed to prevent browser crashes from large files like sonne.json

/**
 * Save data to localStorage (small data only, no chunking to prevent browser crashes)
 */
function saveToLocalStorage<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return;
  
  try {
    const fullKey = CACHE_KEY_PREFIX + key;
    const dataString = compressData(data);
    
    // Prevent saving large data that could crash browser
    if (dataString.length > 1024 * 1024) { // 1MB limit
      console.warn(`[DataService] Skipping localStorage save for ${key}: ${(dataString.length / 1024 / 1024).toFixed(2)}MB is too large (crashes browser)`);
      return;
    }
    
    localStorage.setItem(fullKey, dataString);
    
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('[DataService] LocalStorage quota exceeded, clearing old cache');
      clearOldCache();
    } else {
      console.warn('Error saving to localStorage:', error);
    }
  }
}

/**
 * Clear old localStorage cache entries to free up space
 */
function clearOldCache(): void {
  if (typeof window === 'undefined') return;
  
  try {
    const keysToRemove: string[] = [];
    
    // Find all econtrol cache keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all cache keys
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore individual removal errors
      }
    });
    
    console.log(`[DataService] Cleared ${keysToRemove.length} cache entries`);
  } catch (error) {
    console.warn('[DataService] Error clearing cache:', error);
  }
}

/**
 * Load and cache Gemeinde Online data with KV database and localStorage support
 */
export async function loadGemeindenOnlineData(): Promise<CachedGemeindeOnlineData> {
  const cacheKey = 'gemeinde_online_data';
  
  // Check localStorage first
  const localData = getFromLocalStorage<CachedGemeindeOnlineData>(cacheKey);
  if (localData) {
    dataCache[cacheKey] = localData; // Also cache in memory
    return localData;
  }
  
  // Check in-memory cache
  const cachedData = dataCache[cacheKey] as CachedGemeindeOnlineData | undefined;
  if (cachedData && cachedData.timestamp > Date.now() - GEMEINDE_CACHE_TTL) {
    return cachedData;
  }

  try {
    let data: GemeindenOnlineFile;
    let dataSource: 'kv' | 'local' = 'local';
    
    // Load from local file as single source of truth
    console.log('[DataService] Loading gemeinden data from local file (single source of truth)...');
    data = await loadJsonData<GemeindenOnlineFile>(JSON_DATA_PATHS.gemeindenOnline);
    dataSource = 'local';

    const result: CachedGemeindeOnlineData = {
      ...data,
      timestamp: Date.now(),
      dataSource
    };

    // Cache in memory and localStorage
    dataCache[cacheKey] = result;
    saveToLocalStorage(cacheKey, result);

    return result;
  } catch (error) {
    console.error('Error loading Gemeinde Online data:', error);
    return { 
      timestamp: 0,
      dataSource: 'local'
    };
  }
}

/**
 * Load and cache Gemeinde data with localStorage support (legacy)
 */
export async function loadGemeindenData(): Promise<CachedGemeindeData> {
  const cacheKey = 'gemeinde_data';
  
  // Check localStorage first
  const localData = getFromLocalStorage<CachedGemeindeData>(cacheKey);
  if (localData) {
    dataCache[cacheKey] = localData; // Also cache in memory
    return localData;
  }
  
  // Check in-memory cache
  const cachedData = dataCache[cacheKey] as CachedGemeindeData | undefined;
  if (cachedData && cachedData.timestamp > Date.now() - GEMEINDE_CACHE_TTL) {
    return cachedData;
  }

  try {

    const data = await loadJsonData<GemeindeDataFile>(JSON_DATA_PATHS.gemeindeData);

    const result: CachedGemeindeData = {
      ...data,
      timestamp: Date.now()
    };

    // Cache in memory and localStorage
    dataCache[cacheKey] = result;
    saveToLocalStorage(cacheKey, result);

    // No need for success log - the data is now available
    return result;
  } catch (error) {
    console.error('Error loading Gemeinde data:', error);
    return { 
      gemeinden: [], 
      statistics: {
        totalGemeinden: 0,
        totalEinwohner: 0,
        totalHaushalte: 0,
        totalPlz: 0,
        generatedAt: new Date().toISOString()
      },
      timestamp: 0
    };
  }
}

/**
 * Convert GemeindeOnlineData to legacy GemeindeData format for backward compatibility
 */
export function convertOnlineToLegacyFormat(
  gemeindeNummer: string, 
  onlineData: GemeindeOnlineData
): GemeindeData {
  return {
    plv: onlineData.plv || '',
    gemeindeNummer,
    name: onlineData.name,
    flaecheHa: onlineData.flaeche_ha,
    haushalte: onlineData.haushalte,
    einwohner: onlineData.einwohnerzahl,
    flaecheKm2: onlineData.flaeche_ha / 100, // Convert ha to km²
    einwohnerDichte: onlineData.einwohnerDichte || 0,
    isBezirk: onlineData.isBezirk || false,
    bezirkName: onlineData.bezirkName || onlineData.bezirk,
    bezirkPLV: onlineData.plv || '',
    plzList: onlineData.postleitzahlen,
    wappenLink: (onlineData as any).wappenLink // Add wappenLink to legacy format
  };
}

/**
 * Search gemeinden by name in the online data structure
 */
export function searchGemeindenOnline(
  gemeindenData: GemeindenOnlineFile,
  searchName: string
): { gemeindeNummer: string; data: GemeindeOnlineData } | null {
  const searchLower = searchName.toLowerCase().trim();
  
  for (const [gemeindeNummer, data] of Object.entries(gemeindenData)) {
    if (data.name.toLowerCase() === searchLower) {
      return { gemeindeNummer, data };
    }
  }
  
  return null;
}

/**
 * Get energy data from integrated online structure (avoiding separate API calls)
 */
export function getIntegratedEnergyData(onlineData: GemeindeOnlineData): {
  solarKwh: number;
  waterKwh: number;
  biomassKwh: number;
  biogasKwh: number;
  totalKwh: number;
} {
  return {
    solarKwh: onlineData.pv_kwh_2024 || 0,
    waterKwh: onlineData.wk_kwh_2024 || 0,
    biomassKwh: onlineData.bm_kwh_2024 || 0,
    biogasKwh: onlineData.bg_kwh_2024 || 0,
    totalKwh: onlineData.total_kwh_2024 || 0
  };
}

/**
 * Clear all cached energy data (useful for development or when new data is available)
 */
export function clearEnergyCache(): void {
  if (typeof window === 'undefined') return;
  
  const energyTypes: EnergyType[] = ['wasser', 'sonne', 'biogas', 'biomasse'];
  
  // Clear localStorage
  energyTypes.forEach(type => {
    const cacheKey = `energy_${type}`;
    localStorage.removeItem(CACHE_KEY_PREFIX + cacheKey);
  });
  
  // Clear in-memory cache
  energyTypes.forEach(type => {
    const cacheKey = `energy_${type}`;
    delete dataCache[cacheKey];
  });
}

/**
 * Get cache status for debugging
 */
export function getCacheStatus(): { 
  localStorage: Record<string, { size: number; age: string; expires: string }>;
  memoryCache: Record<string, { age: string; expires: string }>;
} {
  if (typeof window === 'undefined') return { localStorage: {}, memoryCache: {} };
  
  const energyTypes: EnergyType[] = ['wasser', 'sonne', 'biogas', 'biomasse'];
  const result = { localStorage: {}, memoryCache: {} };
  
  // Check localStorage
  energyTypes.forEach(type => {
    const cacheKey = `energy_${type}`;
    const cached = localStorage.getItem(CACHE_KEY_PREFIX + cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const age = Date.now() - parsed.timestamp;
        const expires = getEnergyDataExpirationTime();
        
        result.localStorage[type] = {
          size: Math.round(cached.length / 1024), // KB
          age: `${Math.round(age / (1000 * 60 * 60 * 24))} days`,
          expires: new Date(expires).toLocaleDateString('de-AT')
        };
      } catch (e) {
        result.localStorage[type] = { size: 0, age: 'invalid', expires: 'invalid' };
      }
    }
  });
  
  // Check memory cache
  energyTypes.forEach(type => {
    const cacheKey = `energy_${type}`;
    const cached = dataCache[cacheKey] as CachedEnergyData | undefined;
    if (cached) {
      const age = Date.now() - cached.timestamp;
      const expires = getEnergyDataExpirationTime();
      
      result.memoryCache[type] = {
        age: `${Math.round(age / (1000 * 60 * 60 * 24))} days`,
        expires: new Date(expires).toLocaleDateString('de-AT')
      };
    }
  });
  
  return result;
}

// No need to re-export cache variables
